import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import reisloLogo from '../../img/logo-prose-captain.svg';
import backgroundImage from '../../img/bg-login-app.jpg';
import { Snackbar } from '../../ui/atoms';
import axios from 'axios';
import { DIRECTUS_INSTANCE } from '../../api';

import { postAuthEndpoint } from '../../features/design-studio';
import {
  BackToWordPressLink,
  WP_CONNECT_STAGE_LABELS,
  captureWpConnect,
  completeWpConnect
} from '../../features/wp-connect';

const shouldRequirePasswordMode = (status, data) => {
  const rawMessage = String(
    data?.errors?.[0]?.message ||
    data?.error ||
    data?.message ||
    ''
  ).toLowerCase();

  return Boolean(
    data?.require_password_login ||
    data?.data?.require_password_login ||
    data?.error_code === 'PASSWORD_REQUIRED' ||
    data?.code === 'PASSWORD_REQUIRED' ||
    (status >= 400 && (rawMessage.includes('administrator') || rawMessage.includes('admin') || rawMessage.includes('password')))
  );
};

const extractDirectusErrorMessage = (data, fallback) => (
  data?.errors?.[0]?.message || data?.error || data?.message || fallback
);

const SignIn = ({ onSignIn }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [step, setStep] = useState('email');
  const [code, setCode] = useState(['', '', '', '']);
  const [wpConnectStage, setWpConnectStage] = useState('idle');
  const turnstileContainerRef = useRef(null);
  const codeInputRefs = useRef([]);
  const codeFormRef = useRef(null);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const wpPending = useMemo(() => captureWpConnect(searchParams), [searchParams]);
  const isWpConnectFlow = Boolean(wpPending);

  useEffect(() => {
    const prefilledEmail = (searchParams.get('email') || '').trim();
    const adminMode = searchParams.get('admin') === '1';
    if (prefilledEmail && !email) {
      setEmail(prefilledEmail);
    }
    if (adminMode) {
      setPasswordMode(true);
    }
  }, [email, searchParams]);

  // Someone without a workspace yet goes through onboarding, which sends them back to WordPress.
  const finishWpConnect = async (authToken) => {
    setWpConnectStage('loading_user');
    const result = await completeWpConnect(authToken, wpPending, { onStageChange: setWpConnectStage });
    if (result === 'needs_onboarding') {
      onSignIn(authToken);
    }
  };

  useEffect(() => {
    const existingToken = localStorage.getItem('directus_token') || localStorage.getItem('authToken');
    if (!existingToken) return;

    if (isWpConnectFlow) {
      setLoading(true);
      setWpConnectStage('loading_user');
      completeWpConnect(existingToken, wpPending, { onStageChange: setWpConnectStage })
        .then((result) => {
          if (result === 'needs_onboarding') {
            navigate('/writer/onboarding');
          }
        })
        .catch(() => {
          setLoading(false);
          setWpConnectStage('idle');
        });
      return;
    }

    navigate('/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isWpConnectFlow]);

  useEffect(() => {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isProduction = hostname.includes('prosecaptain.com');

    if (isLocalhost || !isProduction) {
      setTurnstileToken('dummy-token-for-staging');
      return;
    }

    if (turnstileContainerRef.current) {
      turnstileContainerRef.current.innerHTML = '';
    }

    const initTurnstile = () => {
      if (window.turnstile && process.env.REACT_APP_TURNSTILE_SITE_KEY && turnstileContainerRef.current) {
        try {
          window.turnstile.render(turnstileContainerRef.current, {
            sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
            callback: function(token) {
              setTurnstileToken(token);
            },
            'error-callback': function() {
              setTurnstileToken('turnstile-error-fallback');
            },
            theme: 'light',
            'refresh-expired': 'auto'
          });
        } catch (error) {
          setTurnstileToken('turnstile-render-error-fallback');
        }
      } else {
        setTimeout(() => {
          if (!turnstileToken) {
            setTurnstileToken('turnstile-unavailable-fallback');
          }
        }, 5000);
      }
    };

    if (window.turnstile) {
      initTurnstile();
    } else {
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          initTurnstile();
        }
      }, 500);

      setTimeout(() => {
        clearInterval(checkTurnstile);
        if (!turnstileToken) {
          setTurnstileToken('turnstile-timeout-fallback');
        }
      }, 10000);
    }

    const container = turnstileContainerRef.current;
    return () => {
      if (container) container.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWithPassword = async () => {
    if (!password) {
      throw new Error('Password is required for administrator accounts');
    }

    let directusData = null;
    try {
      const directusResponse = await axios.post(`${DIRECTUS_INSTANCE}/auth/login`, {
        email,
        password
      });
      directusData = directusResponse?.data;
    } catch (directusError) {
      const fallbackStatus = directusError?.response?.status;
      const fallbackData = directusError?.response?.data;
      throw new Error(extractDirectusErrorMessage(fallbackData, fallbackStatus === 401 ? 'Invalid email or password' : 'Login failed'));
    }

    const accessToken = directusData?.data?.access_token;
    if (!accessToken) {
      throw new Error('No access token returned');
    }

    localStorage.setItem('directus_token', accessToken);
    localStorage.setItem('authToken', accessToken);
    if (directusData?.data?.refresh_token) {
      localStorage.setItem('directus_refresh_token', directusData.data.refresh_token);
    }

    if (isWpConnectFlow) {
      await finishWpConnect(accessToken);
      return;
    }

    if (onSignIn) {
      onSignIn(accessToken);
    } else {
      navigate('/');
    }
  };

  const startLoginFlow = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    setShowSnackbar(false);

    try {
      const hostname = window.location.hostname;
      const isProduction = hostname.includes('prosecaptain.com');
      if (isProduction && !turnstileToken) {
        throw new Error('Please complete the security check');
      }

      // Accounts that need a password are detected by /api/auth/login/init (409
      // require_password_login, handled below); no anonymous user lookup in Directus.
      if (passwordMode) {
        await loginWithPassword();
        return;
      }

      const { ok, status, data } = await postAuthEndpoint('/api/auth/login/init', {
        email,
        turnstile_token: turnstileToken
      });

      if (!ok) {
        if (status === 404) {
          navigate(`/signup?email=${encodeURIComponent(email)}`);
          return;
        }
        if (shouldRequirePasswordMode(status, data)) {
          setPasswordMode(true);
          setPassword('');
          setError('');
          return;
        }
        throw new Error(data?.errors?.[0]?.message || 'Failed to send verification code');
      }

      setStep('code');
      setCode(['', '', '', '']);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 0);
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (idx, value) => {
    const digit = String(value || '').replace(/\D/g, '').slice(0, 1);
    const nextCode = [...code];
    nextCode[idx] = digit;
    setCode(nextCode);

    if (digit && idx < 3) {
      codeInputRefs.current[idx + 1]?.focus();
    }
    if (digit && idx === 3 && nextCode.every((part) => part.length === 1)) {
      setTimeout(() => codeFormRef.current?.requestSubmit(), 0);
    }
  };

  const handleCodeKeyDown = (idx, event) => {
    if (event.key === 'Backspace' && !code[idx] && idx > 0) {
      codeInputRefs.current[idx - 1]?.focus();
    }
  };

  const verifyLoginCode = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    setShowSnackbar(false);

    try {
      const fullCode = code.join('');
      if (fullCode.length !== 4) {
        throw new Error('Please enter 4-digit verification code');
      }

      const { ok, data } = await postAuthEndpoint('/api/auth/login/verify', {
        email,
        code: fullCode
      });

      if (!ok) {
        throw new Error(data?.errors?.[0]?.message || data?.error || 'Code verification failed');
      }

      const accessToken = data?.data?.access_token;
      if (!accessToken) {
        throw new Error('No access token returned');
      }

      localStorage.setItem('directus_token', accessToken);
      localStorage.setItem('authToken', accessToken);
      if (data?.data?.refresh_token) {
        localStorage.setItem('directus_refresh_token', data.data.refresh_token);
      }

      if (isWpConnectFlow) {
        await finishWpConnect(accessToken);
        return;
      }

      if (onSignIn) {
        onSignIn(accessToken);
      } else {
        navigate('/');
      }
    } catch (err) {
      const errorMessage = err.message || 'Verification failed';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError('');
    setLoading(true);
    setShowSnackbar(false);
    try {
      const { ok, data } = await postAuthEndpoint('/api/auth/login/init', {
        email,
        turnstile_token: turnstileToken
      });
      if (!ok) {
        throw new Error(data?.errors?.[0]?.message || 'Failed to resend code');
      }
      setCode(['', '', '', '']);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 0);
    } catch (err) {
      const errorMessage = err.message || 'Failed to resend code';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen h-screen flex items-center justify-center px-4 py-12 sm:px-6">
      <div className="absolute inset-0 z-0">
        <img
          alt=""
          src={backgroundImage}
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl px-8 py-6">
          {isWpConnectFlow && <BackToWordPressLink pending={wpPending} className="mb-4" />}
          <div className="text-center">
            <img
              className="h-6 w-auto mx-auto mb-6"
              src={reisloLogo}
              alt="Reislo logo"
            />
            <h2 className="mt-0 text-2xl font-medium text-gray-900">Welcome back!</h2>
            <p className="mt-2 text-base text-gray-500">
              {isWpConnectFlow && step === 'email'
                ? 'Połączymy Twoje konto z workspace WordPress. To może potrwać kilka sekund.'
                : step === 'email'
                ? (passwordMode ? 'Administrator account sign in' : 'Not a member?')
                : `We sent a code to ${email}`}{' '}
              {step === 'email' && !passwordMode && !isWpConnectFlow && (
                <a href="/signup" className="font-semibold text-primary-600 hover:text-primary-500">
                  Register for free
                </a>
              )}
            </p>
          </div>

          <div className="mt-10">
            {step === 'email' ? (
              <form onSubmit={startLoginFlow} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                    Email address
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="form-input-inset"
                    />
                  </div>
                </div>

                {passwordMode && (
                  <div>
                    <label htmlFor="password" className="block text-sm/6 font-medium text-gray-900">
                      Password
                    </label>
                    <div className="mt-2">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="form-input-inset"
                      />
                    </div>
                  </div>
                )}

                {window.location.hostname.includes('prosecaptain.com') && (
                  <div ref={turnstileContainerRef} className="flex justify-center"></div>
                )}

                {isWpConnectFlow && loading && (
                  <div className="rounded-md border border-primary-100 bg-primary-50 px-3 py-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-primary-700">
                      <span className="w-4 h-4 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin"></span>
                      {WP_CONNECT_STAGE_LABELS[wpConnectStage] || WP_CONNECT_STAGE_LABELS.idle}
                    </div>
                    <p className="mt-1 text-xs text-primary-700/80">
                      Trwa bezpieczne łączenie i przygotowanie danych callback.
                    </p>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading || (window.location.hostname.includes('prosecaptain.com') && !turnstileToken)}
                    className={`flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm ${
                      (window.location.hostname.includes('prosecaptain.com') && !turnstileToken)
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                    }`}
                  >
                    {loading ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/70 border-t-white rounded-full animate-spin"></span>
                        <span>{isWpConnectFlow ? 'Łączenie z workspace...' : 'Continuing...'}</span>
                      </span>
                    ) : (window.location.hostname.includes('prosecaptain.com') && !turnstileToken)
                      ? 'Complete Security Check'
                      : (isWpConnectFlow ? 'Połącz i kontynuuj' : 'Continue')}
                  </button>
                </div>

              </form>
            ) : (
              <form ref={codeFormRef} onSubmit={verifyLoginCode} className="space-y-6">
                <div className="flex items-center justify-between gap-2">
                  {code.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => { codeInputRefs.current[idx] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(idx, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                      className="w-12 h-14 text-center text-xl border border-gray-300 rounded-md outline-none focus:border-primary-500"
                    />
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full justify-center rounded-md bg-primary-600 hover:bg-primary-500 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify and sign in'}
                </button>
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={loading}
                  className="flex w-full justify-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Resend code
                </button>
              </form>
            )}

            {error && (
              <div className="text-center text-red-500 text-sm mt-4">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>

      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type="error"
          onClose={() => setShowSnackbar(false)}
        />
      )}
    </div>
  );
};

export default SignIn;
